import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Handshake,
  MoreHorizontal,
  RefreshCw,
  Ban,
  CheckCircle,
  PhoneOutgoing,
  Activity,
} from 'lucide-react'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/CopyButton'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CreatePartnerDialog } from '@/components/settings/CreatePartnerDialog'
import { CredentialsDialog } from '@/components/partner/CredentialsDialog'
import { SipTrunkDialog } from '@/components/partner/SipTrunkDialog'
import { adminPartnersClient } from '@/lib/api'
import {
  PartnerStatus,
  type PartnerResponseDto,
  type GeneratePartnerCredentialsResponseDto,
} from '@super/shared-types'

const formatDate = (dateString: string | Date) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'default'
    case 'SUSPENDED':
      return 'secondary'
    case 'REVOKED':
      return 'destructive'
    default:
      return 'outline'
  }
}

export function PartnersSettingsPage() {
  const { isSuperAdmin, isInitialized } = useAppAuth()
  const [partners, setPartners] = useState<PartnerResponseDto[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [credentialsDialog, setCredentialsDialog] = useState<{
    open: boolean
    credentials: GeneratePartnerCredentialsResponseDto | null
    partnerId: string | null
  }>({
    open: false,
    credentials: null,
    partnerId: null,
  })
  const [sipTrunkDialog, setSipTrunkDialog] = useState<{
    open: boolean
    partner: PartnerResponseDto | null
  }>({ open: false, partner: null })

  useEffect(() => {
    if (!isInitialized || !isSuperAdmin) return
    loadPartners()
  }, [isSuperAdmin, isInitialized])

  const loadPartners = async () => {
    try {
      setLoading(true)
      const data = await adminPartnersClient.listPartners()
      setPartners(data)
    } catch (error) {
      toast.error('Failed to load partners')
      setPartners([])
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateCredentials = async (partner: PartnerResponseDto) => {
    // Only show confirmation if regenerating existing credentials
    if (partner.clientId) {
      const message = `This will revoke the existing credentials for ${partner.name}. The partner will need to update their integration with the new credentials. This action cannot be undone.\n\nAre you sure you want to continue?`
      if (!confirm(message)) {
        return
      }
    }

    try {
      const credentials =
        await adminPartnersClient.generateCredentialsForPartner(partner.id)
      setCredentialsDialog({
        open: true,
        credentials,
        partnerId: partner.id,
      })
      await loadPartners() // Refresh list to show updated credentials
      toast.success('Credentials generated successfully')
    } catch (error) {
      toast.error('Failed to generate credentials')
    }
  }

  const handleUpdateStatus = async (
    partner: PartnerResponseDto,
    status: PartnerStatus,
  ) => {
    const message =
      status === PartnerStatus.ACTIVE
        ? `This will restore ${partner.name}'s access. They will be able to authenticate and provision companies again.\n\nAre you sure you want to continue?`
        : `This will suspend ${partner.name}'s access. They will no longer be able to authenticate or provision companies. You can reactivate them later.\n\nAre you sure you want to continue?`

    if (!confirm(message)) {
      return
    }

    try {
      await adminPartnersClient.updatePartnerStatus(partner.id, status)
      await loadPartners()
      toast.success(
        `Partner ${status === PartnerStatus.ACTIVE ? 'activated' : 'deactivated'} successfully`,
      )
    } catch (error) {
      toast.error('Failed to update partner status')
    }
  }

  const handleSaveSipTrunkId = async (
    partner: PartnerResponseDto,
    sipTrunkId: string | null,
  ) => {
    try {
      await adminPartnersClient.updatePartnerSipTrunkId(partner.id, sipTrunkId)
      await loadPartners()
      toast.success(
        sipTrunkId
          ? 'SIP trunk updated'
          : 'SIP trunk cleared (falls back to env default)',
      )
    } catch (error) {
      // Surface to the caller (SipTrunkDialog) so the dialog stays open on
      // failure — toast alone is misleading when the dialog auto-closes.
      toast.error('Failed to update SIP trunk')
      throw error
    }
  }

  const handlePartnerCreated = () => {
    setCreateDialogOpen(false)
    loadPartners()
    toast.success('Partner created successfully')
  }

  const handleCloseCredentialsDialog = () => {
    setCredentialsDialog({ open: false, credentials: null, partnerId: null })
  }

  if (!isInitialized || !isSuperAdmin) {
    return null
  }

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
            <PageHeader title="Manage partner accounts and credentials" />
            {partners && partners.length > 0 && (
              <Button
                onClick={() => setCreateDialogOpen(true)}
                variant="form"
                size="sm"
                className="h-9 w-fit shrink-0"
              >
                Create partner
              </Button>
            )}
          </div>

          <CreatePartnerDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onSuccess={handlePartnerCreated}
          />

          <CredentialsDialog
            open={credentialsDialog.open}
            onOpenChange={() => handleCloseCredentialsDialog()}
            credentials={credentialsDialog.credentials}
            title="Partner Credentials Generated"
          />

          {sipTrunkDialog.partner && (
            <SipTrunkDialog
              open={sipTrunkDialog.open}
              onOpenChange={open =>
                setSipTrunkDialog(prev => ({ ...prev, open }))
              }
              partnerName={sipTrunkDialog.partner.name}
              currentTrunkId={sipTrunkDialog.partner.sipTrunkId}
              onSave={async sipTrunkId => {
                if (!sipTrunkDialog.partner) return
                await handleSaveSipTrunkId(sipTrunkDialog.partner, sipTrunkId)
              }}
            />
          )}

          <Card>
            <CardContent className="p-0">
              {loading || partners === null ? (
                <div className="space-y-0">
                  <div className="border-b">
                    <div className="flex space-x-4 px-6 py-3.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <div className="divide-y">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex space-x-4 px-6 py-4">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : partners.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Handshake className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No partners yet
                  </h3>
                  <p className="mb-6 max-w-sm text-sm text-muted-foreground">
                    Create a partner account to allow provisioning customer
                    companies via the API
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    Create partner
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner name</TableHead>
                      <TableHead>Integration type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Client ID</TableHead>
                      <TableHead>SIP trunk</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[100px]" align="center">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map(partner => (
                      <TableRow key={partner.id}>
                        <TableCell className="font-medium">
                          {partner.name}
                        </TableCell>
                        <TableCell>{partner.integrationType}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(partner.status)}>
                            {partner.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {partner.clientId ? (
                            <div className="flex items-center gap-2">
                              <code className="font-mono text-sm">
                                {partner.clientId.substring(0, 20)}...
                              </code>
                              <CopyButton
                                value={partner.clientId}
                                variant="ghost"
                                size="sm"
                              />
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              Not generated
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {partner.sipTrunkId ? (
                            <div className="flex items-center gap-2">
                              <code className="font-mono text-sm">
                                {partner.sipTrunkId}
                              </code>
                              <CopyButton
                                value={partner.sipTrunkId}
                                variant="ghost"
                                size="sm"
                              />
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              Not set
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(partner.createdAt)}</TableCell>
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleGenerateCredentials(partner)
                                }
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                {partner.clientId
                                  ? 'Regenerate'
                                  : 'Generate'}{' '}
                                credentials
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setSipTrunkDialog({ open: true, partner })
                                }
                              >
                                <PhoneOutgoing className="w-4 h-4 mr-2" />
                                {partner.sipTrunkId ? 'Edit' : 'Set'} SIP trunk
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link
                                  to={`/partner/partners/${partner.id}/activity`}
                                >
                                  <Activity className="w-4 h-4 mr-2" />
                                  View activity
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {partner.status === PartnerStatus.ACTIVE ? (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleUpdateStatus(
                                      partner,
                                      PartnerStatus.SUSPENDED,
                                    )
                                  }
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleUpdateStatus(
                                      partner,
                                      PartnerStatus.ACTIVE,
                                    )
                                  }
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Activate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>
    </div>
  )
}
